import React               from 'react';
import Alert               from '@widesk-ui/components/Alert';
import Popover             from '@widesk-ui/components/Popover';
import Tooltip             from '@widesk-ui/components/Tooltip';
import { CommonGateProps } from './types';
import Crown               from './Crown';
import PendingMark         from './PendingMark';

type GateFallbackProps = CommonGateProps & {
	message: string;
	/** Valeur d'abonnement pas encore lue : on bride sans rien affirmer (ni couronne, ni message). */
	loading?: boolean;
	renderDetails?: (close: () => void) => React.ReactNode;
};

const OPENING_KEYS = ['Enter', ' '];

/** Masque, alerte ou désactive les enfants selon la valeur de `fallback`. */
const GateFallback = ({ fallback = 'tooltip', message, fullWidth, loading, renderDetails, children }: GateFallbackProps) => {
	const [isOpen, setIsOpen] = React.useState(false);

	if (fallback === 'hide') return null;

	if (fallback === 'alert') {
		return loading ? null : <Alert type="warning" message={message} description={renderDetails?.(() => undefined)} showIcon />;
	}

	const disabledChild = (
		<span
			style={{
				pointerEvents: 'none',
				opacity: 0.4,
				flex: fullWidth ? 1 : undefined,
				minWidth: fullWidth ? 0 : undefined,
			}}
		>
			{children}
		</span>
	);

	const wrapperStyle: React.CSSProperties = {
		display: fullWidth ? 'flex' : 'inline-flex',
		alignItems: 'center',
		gap: 6,
		width: fullWidth ? '100%' : undefined,
	};

	if (loading && fallback !== 'disable') {
		return (
			<span aria-busy style={{ ...wrapperStyle, cursor: 'progress' }}>
				{disabledChild}
				<PendingMark />
			</span>
		);
	}

	// `disable` = désactivation muette, sans explication : le seul repli qui n'affiche pas la couronne.
	if (fallback === 'disable') {
		return (
			<span style={{ cursor: 'not-allowed', display: fullWidth ? 'flex' : 'inline-flex', width: fullWidth ? '100%' : undefined }}>
				{disabledChild}
			</span>
		);
	}

	if (!renderDetails) {
		return (
			<Tooltip title={message}>
				<span role="img" aria-label={message} style={{ ...wrapperStyle, cursor: 'not-allowed' }}>
					{disabledChild}
					<Crown />
				</span>
			</Tooltip>
		);
	}

	return (
		<Tooltip title={isOpen ? undefined : message}>
			<Popover
				trigger="click"
				placement="bottom"
				open={isOpen}
				onOpenChange={setIsOpen}
				content={<div style={{ maxWidth: 320 }}>{renderDetails(() => setIsOpen(false))}</div>}
			>
				<span
					role="button"
					tabIndex={0}
					aria-haspopup="dialog"
					aria-expanded={isOpen}
					aria-label={message}
					style={{ ...wrapperStyle, cursor: 'pointer' }}
					onKeyDown={event => {
						if (!OPENING_KEYS.includes(event.key)) return;

						event.preventDefault();
						setIsOpen(true);
					}}
				>
					{disabledChild}
					<Crown />
				</span>
			</Popover>
		</Tooltip>
	);
};

export default GateFallback;
